'use client';
import React from 'react';
// import Image from 'next/image';
import { useParams } from 'next/navigation';

// import rightImage from '../../../../public/vendor/update-2.png';
// import leftImage from '../../../../public/vendor/update-1.png';
import TermsAndCondition from '@/components/shared/TermsAndCondition';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslations } from 'next-intl';
import { SectionHeader } from '@/components/ui/Typography';
import clsx from 'clsx';
import { B2BContent } from '@/lib/types/customer/B2BContent';
import { askSupport } from '@/clients/customer/ask-support';
import { askSupportSchema } from '@/lib/validations/customer/askSupportSchema';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

type AskSupportFormData = z.infer<ReturnType<typeof askSupportSchema>>;

type Props = {
  content?: B2BContent['email_subs_section'];
  contentClass?: string;
  lang: 'en' | 'de';
  showForm?: boolean;
};

export default function VendorGetUpdate({
  content,
  lang,
  contentClass,
  showForm = false,
}: Props) {
  const [successMessage, setSuccessMessage] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [showSuccess, setShowSuccess] = React.useState<boolean>(false);
  const locale = useParams().locale;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const t = useTranslations();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<AskSupportFormData>({
    resolver: zodResolver(askSupportSchema(lang === 'de' ? 'de' : 'en')),
  });

  const contentWidthClasses = contentClass ?? 'lg:max-w-[50%]';

  const onSubmit: SubmitHandler<AskSupportFormData> = async (data) => {
    setSuccessMessage('');
    setIsSubmitting(true);

    try {
      const token = await executeRecaptcha?.('ask_support');
      const payload = {
        ...data,
        'g-recaptcha-response': token,
      };

      const response = await askSupport(payload, {
        ['Accept-Language']: locale,
      });

      const message = response.data.message || 'Request submitted successfully';
      setSuccessMessage(message);
      setShowSuccess(true);
      reset(); // Reset form on success
    } catch (err: any) {
      if (err.response?.data?.errors) {
        // Set field-specific errors
        Object.entries(err.response.data.errors).forEach(([field, message]) => {
          setError(field as keyof AskSupportFormData, {
            message: message as string,
          });
        });
      }
      setError('root', {
        message: err.response?.data?.message || 'An error occurred',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="update" className="bg-primary py-8 text-white lg:py-20">
      <div className="container">
        <div className="flex flex-col items-center space-y-12">
          {/* <div className="relative h-[323px] w-[400px] lg:mb-[300px] lg:max-w-[25%] lg:self-start">
            <Image
              src={leftImage}
              fill
              sizes="100vw"
              className="floating object-cover"
              alt="update-left-image"
            />
          </div> */}

          <div
            className={clsx('text-center lg:self-center', contentWidthClasses)}
          >
            <div className="section-header flex flex-col items-center justify-center">
              <p className="text-lg font-normal text-white opacity-90">
                {content?.pre_title?.[lang] ?? ''}
              </p>
              <SectionHeader className="text-white">
                {content?.title?.[lang] ?? ''}
              </SectionHeader>
              <p className="text-lg text-white opacity-90">
                {content?.sub_title?.[lang] ?? ''}
              </p>
            </div>

            <>
              {/* {env.activeHostedEmbedURL && <ActiveCampaignEmbed formId={31} />}

              <div className="">
                <TermsAndCondition
                  btnText={t('subscribe_now')}
                  lang={locale === 'en-DE' ? 'en' : 'de'}
                  linkClass="font-bold"
                  className="text-white"
                />
              </div> */}
            </>

            {showForm && !showSuccess && (
              <>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="mx-auto mt-[41px] max-w-[574px] lg:mt-[47px]"
                >
                  <div className="space-y-4">
                    {/* Email Field */}
                    <div>
                      <input
                        type="email"
                        placeholder={t('common.email')}
                        aria-label={t('common.email')}
                        {...register('email')}
                        className="w-full rounded-[110px] border-none bg-white px-[30px] py-[12px] text-h6 text-paragraph shadow-[0px_14px_70px_rgba(0,0,0,0.1)] focus:border-none focus:outline-none focus:ring-0"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-white">
                          {errors.email.message}
                        </p>
                      )}
                    </div>

                    {/* Company Field */}
                    <div>
                      <input
                        type="text"
                        placeholder={t('common.company')}
                        aria-label={t('common.company')}
                        {...register('company')}
                        className="w-full rounded-[110px] border-none bg-white px-[30px] py-[12px] text-h6 text-paragraph shadow-[0px_14px_70px_rgba(0,0,0,0.1)] focus:border-none focus:outline-none focus:ring-0"
                      />
                      {errors.company && (
                        <p className="mt-1 text-sm text-white">
                          {errors.company.message}
                        </p>
                      )}
                    </div>

                    {/* Contact Person Field */}
                    <div>
                      <input
                        type="text"
                        placeholder={t('common.contact_person')}
                        aria-label={t('common.contact_person')}
                        {...register('contact_person')}
                        className="w-full rounded-[110px] border-none bg-white px-[30px] py-[12px] text-h6 text-paragraph shadow-[0px_14px_70px_rgba(0,0,0,0.1)] focus:border-none focus:outline-none focus:ring-0"
                      />
                      {errors.contact_person && (
                        <p className="mt-1 text-sm text-white">
                          {errors.contact_person.message}
                        </p>
                      )}
                    </div>

                    {/* Phone Field (Optional) */}
                    <div>
                      <input
                        type="tel"
                        placeholder={`${t('common.phone')} (${t('optional')})`}
                        aria-label={`${t('common.phone')} (${t('optional')})`}
                        {...register('phone')}
                        className="w-full rounded-[110px] border-none bg-white px-[30px] py-[12px] text-h6 text-paragraph shadow-[0px_14px_70px_rgba(0,0,0,0.1)] focus:border-none focus:outline-none focus:ring-0"
                      />
                      {errors.phone && (
                        <p className="mt-1 text-sm text-white">
                          {errors.phone.message}
                        </p>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-center">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="shrink-0 rounded-[35px] border-2 border-transparent bg-white px-[20px] py-[11.5px] text-base text-primary-800 transition-colors duration-300 hover:border-2 hover:drop-shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSubmitting
                          ? lang === 'de'
                            ? 'Wird gesendet...'
                            : 'Submitting...'
                          : t('common.request_now')}
                      </button>
                    </div>

                    {/* Form Error */}
                    {errors.root && (
                      <div className="text-center">
                        <p className="text-sm text-red-300">
                          {errors.root.message}
                        </p>
                      </div>
                    )}
                  </div>
                </form>
              </>
            )}

            {showForm && !showSuccess && (
              <div className="mt-8">
                <TermsAndCondition
                  btnText={t('common.request_now')}
                  className="text-white"
                  linkclassName="text-white font-bold"
                  lang={locale === 'en-DE' ? 'en' : 'de'}
                />
              </div>
            )}

            {showForm && showSuccess && (
              <div className="mx-auto mt-[41px] max-w-[574px] text-center lg:mt-[47px]">
                <div className="rounded-[110px] bg-white px-[30px] py-[12px] shadow-[0px_14px_70px_rgba(0,0,0,0.1)]">
                  <p className="text-lg font-medium text-primary-800">
                    {successMessage}
                  </p>
                </div>
              </div>
            )}
          </div>
          <p className="text-center text-h6">
            <span className="text-lg text-white opacity-90">
              {content?.phone_label?.[lang] ?? ''}
            </span>
            <br />
            <span
              dangerouslySetInnerHTML={{
                __html: content?.phone || t('vendor_page.get_update.subheader'),
              }}
            />
          </p>
          {/* <div className="relative h-[333px] w-[411px] lg:max-w-[25%] lg:self-end">
            <Image
              src={rightImage}
              fill
              sizes="100vw"
              className="object-cover"
              alt="update-right-image"
            />
          </div> */}
        </div>
      </div>
    </div>
  );
}
